import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc,
  setDoc,
  updateDoc, 
  deleteField,
  query, 
  orderBy, 
  limit,
  Timestamp,
  where 
} from 'firebase/firestore';
import { db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { endPartyClient } from '../services/partyLifecycle';

interface UserRecord {
  uid: string;
  email: string;
  displayName: string;
  createdAt: Date;
  lastLoginAt: Date;
  blocked?: boolean;
  blockedReason?: string;
  partyPass?: {
    active: boolean;
    expiresAt: Date;
    purchasedAt: Date;
  };
  proSubscription?: {
    active: boolean;
    expiresAt: Date;
    purchasedAt: Date;
  };
  youtubeToken?: string;
}

interface PartyRecord {
  id: string;
  name: string;
  createdAt: Date;
  hostId: string;
  hostEmail: string;
  queueCount: number;
  isActive: boolean;
}

interface PaymentRecord {
  id: string;
  userId: string;
  userEmail: string;
  type: string;
  amount: number;
  status: string;
  createdAt: Date;
}

interface FeedbackMessageRecord {
  id: string;
  category: 'feedback' | 'feature';
  title: string;
  message: string;
  email?: string | null;
  sourcePath?: string | null;
  status: 'new' | 'reviewed' | 'closed';
  createdAt: Date;
  userId?: string | null;
  userDisplayName?: string | null;
}

const AdminPage: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'users' | 'parties' | 'payments' | 'stats' | 'ads' | 'messages'>('stats');
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [parties, setParties] = useState<PartyRecord[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [feedbackMessages, setFeedbackMessages] = useState<FeedbackMessageRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserRecord | null>(null);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserIsAdmin, setNewUserIsAdmin] = useState(false);
  const [endingPartyId, setEndingPartyId] = useState<string | null>(null);
  
  // Redirect if not admin
  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  // Load data when tab changes
  useEffect(() => {
    loadTabData();
  }, [activeTab]);

  const loadTabData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'stats':
          await Promise.all([loadUsers(), loadPayments()]);
          break;
        case 'users':
          await loadUsers();
          break;
        case 'parties':
          await loadParties();
          break;
        case 'payments':
          await loadPayments();
          break;
        case 'ads':
          break;
        case 'messages':
          await loadFeedbackMessages();
          break;
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const loadUsers = async () => {
    const usersQuery = query(
      collection(db, 'users'),
      orderBy('lastLoginAt', 'desc'),
      limit(100)
    );
    const snapshot = await getDocs(usersQuery);
    const usersList = snapshot.docs.map(doc => ({
      uid: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      lastLoginAt: doc.data().lastLoginAt?.toDate(),
      partyPass: doc.data().partyPass ? {
        ...doc.data().partyPass,
        expiresAt: doc.data().partyPass.expiresAt?.toDate(),
        purchasedAt: doc.data().partyPass.purchasedAt?.toDate()
      } : undefined,
      proSubscription: doc.data().proSubscription ? {
        ...doc.data().proSubscription,
        expiresAt: doc.data().proSubscription.expiresAt?.toDate(),
        purchasedAt: doc.data().proSubscription.purchasedAt?.toDate()
      } : undefined
    })) as UserRecord[];
    setUsers(usersList);
  };

  const handleAddUser = async () => {
    if (!newUserEmail) {
      alert('Email is required');
      return;
    }
    try {
      const functions = getFunctions(undefined, 'us-central1');
      const adminCreateUser = httpsCallable(functions, 'adminCreateUser');
      await adminCreateUser({ email: newUserEmail, displayName: newUserName, makeAdmin: newUserIsAdmin });
      setNewUserEmail('');
      setNewUserName('');
      setNewUserIsAdmin(false);
      await loadUsers();
      alert('User added successfully');
    } catch (e: any) {
      console.error('Add user error:', e);
      alert(e?.message || 'Failed to add user');
    }
  };

  const endParty = async (partyId: string) => {
    if (!window.confirm('End this party now?')) return;
    try {
      setEndingPartyId(partyId);
      await endPartyClient(partyId);
      await loadParties();
    } catch (e: any) {
      console.error('End party error:', e);
      alert(e?.message || 'Failed to end party');
    } finally {
      setEndingPartyId(null);
    }
  };
  const loadParties = async () => {
    const partiesQuery = query(
      collection(db, 'parties'),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    const snapshot = await getDocs(partiesQuery);
    const partiesList = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate()
    })) as PartyRecord[];
    setParties(partiesList);
  };

  const loadPayments = async () => {
    const paymentsQuery = query(
      collection(db, 'payments'),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    const snapshot = await getDocs(paymentsQuery);
    const paymentsList = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate()
    })) as PaymentRecord[];
    setPayments(paymentsList);
  };

  const loadFeedbackMessages = async () => {
    const feedbackQuery = query(
      collection(db, 'feedback_messages'),
      orderBy('createdAt', 'desc'),
      limit(100)
    );
    const snapshot = await getDocs(feedbackQuery);
    const messages = snapshot.docs.map((entry) => ({
      id: entry.id,
      ...entry.data(),
      createdAt: entry.data().createdAt?.toDate?.() ?? new Date(),
    })) as FeedbackMessageRecord[];
    setFeedbackMessages(messages);
  };

  const updateFeedbackStatus = async (messageId: string, status: FeedbackMessageRecord['status']) => {
    try {
      await updateDoc(doc(db, 'feedback_messages', messageId), { status });
      setFeedbackMessages((prev) => prev.map((item) => (item.id === messageId ? { ...item, status } : item)));
    } catch (error) {
      console.error('Error updating feedback status:', error);
      alert('Failed to update message status');
    }
  };

  const extendMembership = async (userId: string, type: 'party-pass' | 'pro', hours: number) => {
    try {
      const userRef = doc(db, 'users', userId);
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + hours);

      const updateData: any = {};
      if (type === 'party-pass') {
        updateData.partyPass = {
          active: true,
          expiresAt: Timestamp.fromDate(expiresAt),
          purchasedAt: Timestamp.fromDate(new Date())
        };
      } else {
        updateData.proSubscription = {
          active: true,
          expiresAt: Timestamp.fromDate(expiresAt),
          purchasedAt: Timestamp.fromDate(new Date())
        };
      }

      await updateDoc(userRef, updateData);
      alert(`Successfully added ${hours} hours of ${type} to user`);
      loadUsers(); // Refresh data
    } catch (error) {
      console.error('Error extending membership:', error);
      alert('Failed to extend membership');
    }
  };

  const makeUserAdmin = async (userId: string) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { isAdmin: true });
      alert('User granted admin access');
      loadUsers();
    } catch (error) {
      console.error('Error making user admin:', error);
      alert('Failed to grant admin access');
    }
  };

  const revokeMembership = async (userId: string) => {
    if (!window.confirm('Revoke ALL memberships from this user? This cannot be undone.')) return;
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        partyPass: deleteField(),
        proSubscription: deleteField()
      });
      alert('All memberships revoked');
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      console.error('Error revoking membership:', error);
      alert('Failed to revoke membership');
    }
  };

  const blockUser = async (userId: string) => {
    const reason = window.prompt('Reason for blocking this user (e.g., chargeback, abuse):');
    if (reason === null) return; // cancelled
    if (!window.confirm('BLOCK this user? This will:\n- Revoke all memberships\n- Disable their account\n- Prevent them from signing in')) return;
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        blocked: true,
        blockedAt: Timestamp.fromDate(new Date()),
        blockedReason: reason || 'Blocked by admin',
        partyPass: deleteField(),
        proSubscription: deleteField()
      });
      alert('User blocked and memberships revoked');
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      console.error('Error blocking user:', error);
      alert('Failed to block user');
    }
  };

  const unblockUser = async (userId: string) => {
    if (!window.confirm('Unblock this user? They will be able to sign in again.')) return;
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        blocked: deleteField(),
        blockedAt: deleteField(),
        blockedReason: deleteField()
      });
      alert('User unblocked');
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      console.error('Error unblocking user:', error);
      alert('Failed to unblock user');
    }
  };

  const getStats = () => {
    const totalUsers = users.length;
    const now = new Date();
    const activePartyPasses = users.filter(u => u.partyPass?.active && u.partyPass.expiresAt && u.partyPass.expiresAt > now).length;
    const activeProSubs = users.filter(u => u.proSubscription?.active && u.proSubscription.expiresAt && u.proSubscription.expiresAt > now).length;
    const totalRevenue = payments.filter(p => p.status === 'completed').reduce((sum, p) => sum + (p.amount / 100), 0);

    return { totalUsers, activePartyPasses, activeProSubs, totalRevenue };
  };

  if (!isAdmin) {
    return <div>Access Denied</div>;
  }

  const stats = getStats();

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">QrOke Admin Dashboard</h1>
            <p className="text-gray-600">Manage users, memberships, and monitor activity</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/admin/ads')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              Ads Manager
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
            >
              Back to Dashboard
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500">Total Users</h3>
            <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500">Active Party Passes</h3>
            <p className="text-2xl font-bold text-blue-600">{stats.activePartyPasses}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500">Active Pro Subs</h3>
            <p className="text-2xl font-bold text-purple-600">{stats.activeProSubs}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500">Total Revenue</h3>
            <p className="text-2xl font-bold text-green-600">${stats.totalRevenue.toFixed(2)}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'stats', name: 'Statistics' },
                { id: 'users', name: 'Users' },
                { id: 'parties', name: 'Parties' },
                { id: 'payments', name: 'Payments' },
                { id: 'ads', name: 'Ad Management' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {loading && (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}

            {/* Statistics Tab */}
            {activeTab === 'stats' && !loading && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">Platform Statistics</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">User Activity</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Registered Users:</span>
                        <span className="font-medium">{stats.totalUsers}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Active Subscriptions:</span>
                        <span className="font-medium">{stats.activePartyPasses + stats.activeProSubs}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Free Users:</span>
                        <span className="font-medium">{stats.totalUsers - stats.activePartyPasses - stats.activeProSubs}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue Overview</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Revenue:</span>
                        <span className="font-medium text-green-600">${stats.totalRevenue.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Party Pass Revenue:</span>
                        <span className="font-medium">${(stats.activePartyPasses * 5).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Conversion Rate:</span>
                        <span className="font-medium">
                          {stats.totalUsers > 0 ? ((stats.activePartyPasses + stats.activeProSubs) / stats.totalUsers * 100).toFixed(1) : 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && !loading && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-gray-900">User Management</h2>
                <div className="bg-gray-50 rounded-lg p-4 flex items-end gap-3">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} placeholder="user@example.com" className="mt-1 w-full border rounded px-3 py-2" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700">Display Name</label>
                    <input value={newUserName} onChange={(e) => setNewUserName(e.target.value)} placeholder="Display Name" className="mt-1 w-full border rounded px-3 py-2" />
                  </div>
                  <label className="inline-flex items-center gap-2">
                    <input type="checkbox" checked={newUserIsAdmin} onChange={(e) => setNewUserIsAdmin(e.target.checked)} />
                    <span className="text-sm text-gray-700">Make Admin</span>
                  </label>
                  <button onClick={handleAddUser} className="bg-blue-600 text-white px-4 py-2 rounded">Add User</button>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Membership</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.map((user) => (
                        <tr key={user.uid}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{user.displayName}</div>
                              <div className="text-sm text-gray-500">{user.email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {user.blocked ? (
                                <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-semibold">
                                  BLOCKED
                                </span>
                              ) : user.partyPass?.active && user.partyPass.expiresAt > new Date() ? (
                                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                                  Party Pass (expires {user.partyPass.expiresAt.toLocaleDateString()})
                                </span>
                              ) : user.proSubscription?.active && user.proSubscription.expiresAt > new Date() ? (
                                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs">
                                  Pro (expires {user.proSubscription.expiresAt.toLocaleDateString()})
                                </span>
                              ) : (
                                <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs">Free</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {user.lastLoginAt?.toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => setSelectedUser(user)}
                              className="text-blue-600 hover:text-blue-900 mr-4"
                            >
                              Manage
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Parties Tab */}
            {activeTab === 'parties' && !loading && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-gray-900">Recent Parties</h2>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Party Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Host</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {parties.map((party) => (
                        <tr key={party.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {party.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {party.hostEmail}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {party.createdAt?.toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              party.isActive 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {party.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            {party.isActive && (
                              <button
                                onClick={() => endParty(party.id)}
                                className="bg-red-600 hover:bg-red-500 text-white px-3 py-1 rounded text-xs"
                                disabled={endingPartyId === party.id}
                              >
                                {endingPartyId === party.id ? 'Ending…' : 'End'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Payments Tab */}
            {activeTab === 'payments' && !loading && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-gray-900">Recent Payments</h2>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {payments.map((payment) => (
                        <tr key={payment.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {payment.userEmail}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {payment.type}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            ${(payment.amount / 100).toFixed(2)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              payment.status === 'completed'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {payment.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {payment.createdAt?.toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'messages' && !loading && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Feedback Inbox</h2>
                    <p className="text-sm text-gray-500">Messages submitted from the lean feedback form.</p>
                  </div>
                  <button
                    onClick={() => loadFeedbackMessages()}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Refresh
                  </button>
                </div>

                {feedbackMessages.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
                    No messages yet.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {feedbackMessages.map((entry) => (
                      <div key={entry.id} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-700">
                                {entry.category}
                              </span>
                              <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                                entry.status === 'new'
                                  ? 'bg-amber-100 text-amber-800'
                                  : entry.status === 'reviewed'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-emerald-100 text-emerald-800'
                              }`}>
                                {entry.status}
                              </span>
                            </div>
                            <h3 className="mt-3 text-lg font-semibold text-gray-900">{entry.title}</h3>
                            <p className="mt-1 text-xs text-gray-500">
                              {entry.createdAt?.toLocaleString()} · {entry.userDisplayName || entry.email || 'Anonymous'}
                            </p>
                          </div>
                          <select
                            value={entry.status}
                            onChange={(e) => updateFeedbackStatus(entry.id, e.target.value as FeedbackMessageRecord['status'])}
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                          >
                            <option value="new">New</option>
                            <option value="reviewed">Reviewed</option>
                            <option value="closed">Closed</option>
                          </select>
                        </div>

                        <p className="mt-4 whitespace-pre-wrap text-sm leading-6 text-gray-700">{entry.message}</p>

                        <div className="mt-4 flex flex-wrap gap-4 text-xs text-gray-500">
                          <span>From page: {entry.sourcePath || 'Unknown'}</span>
                          {entry.email && <span>Reply-to: {entry.email}</span>}
                          {entry.userId && <span>User ID: {entry.userId}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Ads Tab */}
            {activeTab === 'ads' && !loading && (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-md p-5">
                  <h2 className="text-xl font-semibold text-blue-950">Ad Management Moved</h2>
                  <p className="mt-2 text-sm text-blue-900">
                    Free-tier promos now use the dedicated ads manager with canonical buckets for house promos, core hosting messaging, and sponsor spots.
                  </p>
                  <div className="mt-4 flex gap-3">
                    <button
                      onClick={() => navigate('/admin/ads')}
                      className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                    >
                      Open Dedicated Ads Manager
                    </button>
                    <button
                      onClick={() => navigate('/dashboard')}
                      className="rounded-md border border-blue-300 bg-white px-4 py-2 text-sm font-semibold text-blue-900 hover:bg-blue-50"
                    >
                      Back to Dashboard
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* User Management Modal */}
        {selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Manage User: {selectedUser.displayName}</h3>
              <p className="text-sm text-gray-500 mb-4">{selectedUser.email}</p>

              {selectedUser.blocked && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                  <p className="text-sm font-semibold text-red-800">This user is BLOCKED</p>
                  {selectedUser.blockedReason && (
                    <p className="text-sm text-red-600">Reason: {selectedUser.blockedReason}</p>
                  )}
                </div>
              )}
              
              <div className="space-y-4">
                {!selectedUser.blocked && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Add Party Pass Hours:
                      </label>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => extendMembership(selectedUser.uid, 'party-pass', 12)}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm"
                        >
                          +12h
                        </button>
                        <button
                          onClick={() => extendMembership(selectedUser.uid, 'party-pass', 24)}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm"
                        >
                          +24h
                        </button>
                        <button
                          onClick={() => extendMembership(selectedUser.uid, 'party-pass', 168)}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm"
                        >
                          +1 week
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Add Pro Subscription:
                      </label>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => extendMembership(selectedUser.uid, 'pro', 720)}
                          className="bg-purple-600 text-white px-3 py-1 rounded text-sm"
                        >
                          +1 month
                        </button>
                        <button
                          onClick={() => extendMembership(selectedUser.uid, 'pro', 2160)}
                          className="bg-purple-600 text-white px-3 py-1 rounded text-sm"
                        >
                          +3 months
                        </button>
                      </div>
                    </div>

                    <div>
                      <button
                        onClick={() => makeUserAdmin(selectedUser.uid)}
                        className="bg-gray-600 text-white px-4 py-2 rounded text-sm"
                      >
                        Grant Admin Access
                      </button>
                    </div>
                  </>
                )}

                {/* Danger Zone */}
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <p className="text-sm font-medium text-red-600 mb-2">Danger Zone</p>
                  <div className="flex flex-wrap gap-2">
                    {!selectedUser.blocked && (
                      <>
                        <button
                          onClick={() => revokeMembership(selectedUser.uid)}
                          className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded text-sm"
                        >
                          Revoke Membership
                        </button>
                        <button
                          onClick={() => blockUser(selectedUser.uid)}
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm"
                        >
                          Block User
                        </button>
                      </>
                    )}
                    {selectedUser.blocked && (
                      <button
                        onClick={() => unblockUser(selectedUser.uid)}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm"
                      >
                        Unblock User
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <button
                  onClick={() => setSelectedUser(null)}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;